import { CameraCaptureSession } from "./camera";
import { CaptureModal } from "./modal";
import { DEFAULT_CAPTURE_OPTIONS } from "./policy";
import {
  FingerprintCaptureError,
  type FingerprintCaptureOptions,
} from "./types";

/**
 * Opens the built-in capture experience and resolves with a full-frame,
 * quality-approved, full-color PNG Blob from the camera.
 */
export async function captureFingerprint(
  options: FingerprintCaptureOptions = {},
): Promise<Blob> {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new FingerprintCaptureError(
      "UNSUPPORTED_DEVICE",
      "Fingerprint capture is available only in a browser.",
      false,
    );
  }

  const internalAbort = new AbortController();
  const externalAbort = () => internalAbort.abort();
  options.signal?.addEventListener("abort", externalAbort, { once: true });
  const finger = options.finger ?? DEFAULT_CAPTURE_OPTIONS.finger;
  const modal = new CaptureModal(
    () => internalAbort.abort(),
    options.theme,
    options.messages,
    finger,
    options.debug ?? false,
  );

  const session = new CameraCaptureSession(
    modal.video,
    {
      timeoutMs: options.timeoutMs ?? DEFAULT_CAPTURE_OPTIONS.timeoutMs,
      stableDurationMs: Math.max(
        250,
        Math.min(
          3_000,
          options.stableDurationMs ??
            DEFAULT_CAPTURE_OPTIONS.stableDurationMs,
        ),
      ),
      burstFrames: Math.max(
        1,
        Math.min(
          12,
          Math.round(options.burstFrames ?? DEFAULT_CAPTURE_OPTIONS.burstFrames),
        ),
      ),
      cameraFacing:
        options.cameraFacing ?? DEFAULT_CAPTURE_OPTIONS.cameraFacing,
      finger,
    },
    internalAbort.signal,
    ({ guidance, stableProgress, analysis, policy, focusStrategy, phase }) => {
      modal.update(
        guidance,
        stableProgress,
        analysis,
        policy,
        focusStrategy,
        phase,
      );
      options.onGuidance?.(guidance);
    },
    (width, height) => modal.setAspectRatio(width, height),
  );

  try {
    return await session.run();
  } finally {
    session.stop();
    modal.destroy();
    options.signal?.removeEventListener("abort", externalAbort);
  }
}
